import { z } from "zod";
import { changesOf } from "./zod-util";

/** Permits, inspections, warranty (spec §15). */

export interface PermitInspectionItem {
  id: string;
  kind: string;
  scheduledAt: string | null;
  inspector: string;
  result: "pending" | "pass" | "fail";
  notes: string;
  appointmentId: string | null;
}

export interface PermitItem {
  id: string;
  jobId: string;
  number: string;
  city: string;
  type: string;
  costCents: number | null;
  appliedOn: string | null;
  approvedOn: string | null;
  expiresOn: string | null;
  file: { id: string; name: string; url: string } | null;
  notes: string;
  status: "applied" | "approved" | "expired" | "not_applied";
  inspections: PermitInspectionItem[];
}

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable();

export const permitInputSchema = z.object({
  number: z.string().trim().max(60).default(""),
  city: z.string().trim().max(80).default(""),
  type: z.string().trim().max(80).default(""),
  costCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
  appliedOn: day.default(null),
  approvedOn: day.default(null),
  expiresOn: day.default(null),
  fileId: z.string().uuid().nullable().default(null),
  notes: z.string().trim().max(4000).default(""),
});
export const permitUpdateSchema = changesOf(permitInputSchema);

export const inspectionInputSchema = z.object({
  kind: z.string().trim().max(80).default(""),
  scheduledAt: z.string().datetime({ offset: true }).nullable().default(null),
  inspector: z.string().trim().max(120).default(""),
  notes: z.string().trim().max(4000).default(""),
});
export const inspectionUpdateSchema = changesOf(inspectionInputSchema).extend({ result: z.enum(["pending", "pass", "fail"]).optional() });

/** Where a permit stands on `today`. */
export function permitStatus(p: { appliedOn: string | null; approvedOn: string | null; expiresOn: string | null }, today: string): PermitItem["status"] {
  if (p.expiresOn && p.expiresOn < today) return "expired";
  if (p.approvedOn) return "approved";
  if (p.appliedOn) return "applied";
  return "not_applied";
}

/** YYYY-MM-DD plus whole years; Feb 29 becomes Feb 28 in a non-leap year. */
export function addYears(dateIso: string, years: number): string {
  const [y, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  const ty = y + years;
  const last = new Date(Date.UTC(ty, m, 0)).getUTCDate();
  return `${String(ty).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

/**
 * Warranty term (spec §8): 5 years on residential steep-slope roofing, 1 year on everything else.
 * Roofing = the job's workflow is a roof workflow (its name says roof, or it's an insurance claim,
 * which here is roofing); residential = the customer is a person, not a business.
 */
export function warrantyYears(job: { workflowName: string; customerKind: "residential" | "commercial" }, years: { roof: number; other: number }): number {
  const roof = /roof|insurance/i.test(job.workflowName);
  return roof && job.customerKind === "residential" ? years.roof : years.other;
}

export interface WarrantyClaimItem {
  id: string;
  reportedOn: string;
  description: string;
  status: "open" | "resolved" | "denied";
  resolution: string;
}

export interface WarrantyItem {
  id: string;
  jobId: string;
  termYears: number;
  startsOn: string;
  endsOn: string;
  active: boolean;
  manufacturer: string;
  manufacturerRegisteredOn: string | null;
  certificate: { id: string; name: string; url: string } | null;
  notes: string;
  claims: WarrantyClaimItem[];
}

export const warrantyInputSchema = z.object({
  termYears: z.number().int().min(0).max(50).optional(),
  startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  manufacturer: z.string().trim().max(80).optional(),
  manufacturerRegisteredOn: day.optional(),
  notes: z.string().trim().max(4000).optional(),
});

export const warrantyClaimInputSchema = z.object({
  reportedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  description: z.string().trim().min(1, "Describe the problem").max(4000),
});
export const warrantyClaimUpdateSchema = z.object({
  status: z.enum(["open", "resolved", "denied"]).optional(),
  resolution: z.string().trim().max(4000).optional(),
  description: z.string().trim().min(1).max(4000).optional(),
});
